import React, { useEffect, useState } from "react";
import { extractDomain } from "../utils/utils";
import CloseIcon2 from "../icons/CloseIcon2";
import ToolkitIcon from "../icons/ToolkitIcon";

type ScheduleItem = {
  showFrom: string;
  start: string;
  end: string;
  message?: string;
  site?: "seeker" | "recruiter";
};

// {
//   "schedule": {
//     "global": {
//       "showFrom": "2026-03-18T00:00:00Z",
//       "start": "2026-03-19T20:00:00Z",
//       "end": "2026-03-19T22:00:00Z",
//       "message": "Scheduled maintenance on 19 Mar from 8:00 PM to 10:00 PM."
//     },
//   }
// }

type ScheduleMap = {
  global?: ScheduleItem;
} & Record<string, ScheduleItem>;

interface TopBannerProps {
  topBannerStyles?: {
    containerStyles?: React.CSSProperties;
    subContainerStyles?: React.CSSProperties;
  };
  siteContext?: string;
  platform?: "seeker" | "recruiter";
}

const TopBanner: React.FC<TopBannerProps> = ({
  topBannerStyles,
  siteContext,
  platform,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [showPanel, setShowPanel] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [scheduleMap, setScheduleMap] = useState<ScheduleMap | null>(null);
  const [loading, setLoading] = useState(true);

  const CDN_URL = `https://media.foundit.in/edge/recruiter-dashboard/_next/static/media/maintenance.json`;

  const isEmptyObject = (obj: any) =>
    !obj || typeof obj !== "object" || Object.keys(obj).length === 0;

  const isPlatformMatch = (
    schedule: ScheduleItem,
    platform?: "seeker" | "recruiter",
  ): boolean => {
    // If no site restriction, show on both
    if (!schedule.site) return true;

    // Only show if platform matches
    return schedule.site === platform;
  };

  // site contex
  const getSiteContext = () => {
    if (siteContext) return siteContext;
    if (typeof window !== "undefined") {
      return (
        JSON.parse(sessionStorage.getItem("userDetails") || "{}")
          ?.site_context || "monsterindia"
      );
    }
    return "monsterindia";
  };

  // Fetch schedule data
  const fetchSchedule = async () => {
    try {
      // Check if test data exists in localStorage
      if (typeof window !== "undefined") {
        const testDataStr = localStorage?.getItem("BANNER_TEST_SCHEDULE");
        if (testDataStr) {
          const testData = JSON.parse(testDataStr);
          if (testData?.schedule && !isEmptyObject(testData.schedule)) {
            setScheduleMap(testData.schedule);
            setLoading(false);
            return; // use test data instead of CDN
          }
        }
      }

      // Fallback to api fetch
      const res = await fetch(CDN_URL, { cache: "no-store" });
      const json = await res.json();

      if (!json?.schedule || isEmptyObject(json.schedule)) {
        setScheduleMap(null);
      } else {
        setScheduleMap(json.schedule);
      }
    } catch (err) {
      console.error("Banner config fetch failed:", err);
      setScheduleMap(null);
    } finally {
      setLoading(false);
    }
  };

  // Get schedule for current site (site-specific → global fallback)
  const getSchedule = (): ScheduleItem | null => {
    if (isEmptyObject(scheduleMap)) return null;

    const context = getSiteContext();

    // Try site-specific first
    const siteSpecific = scheduleMap?.[context];
    if (siteSpecific) return siteSpecific;

    // Fallback to global
    return scheduleMap?.global || null;
  };

  useEffect(() => {
    setIsMounted(true);

    // Load tempKeys for panel close state
    if (typeof window !== "undefined") {
      const tempKeys = JSON.parse(localStorage?.getItem("tempKeys") || "{}");
      setShowPanel(tempKeys.showPanel !== false);
    }

    fetchSchedule();
  }, []);

  const schedule = getSchedule();

  if (!schedule || loading) return null;

  // Parse times (UTC → local)
  const showFromDateTime = new Date(schedule.showFrom);
  const startDateTime = new Date(schedule.start);
  const endDateTime = new Date(schedule.end);
  const now = new Date();

  // Invalid date guard
  if (
    isNaN(showFromDateTime.getTime()) ||
    isNaN(startDateTime.getTime()) ||
    isNaN(endDateTime.getTime())
  ) {
    return null;
  }

  function format24HourWithAmPm(date: any) {
    if (!(date instanceof Date)) return "";

    const hours = date.getHours(); // 0-23
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours < 12 ? "AM" : "PM";
    const hour24 = hours.toString().padStart(2, "0"); // 00-23

    return `${hour24}:${minutes} ${ampm}`;
  }

  // Formatters
  const formatTime = (date: Date) =>
    date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  const formatDate = (date: Date) => {
    const parts = new Intl.DateTimeFormat("en", {
      day: "2-digit",
      month: "short",
    }).formatToParts(date);

    const day = parts.find((p) => p.type === "day")?.value;
    const month = parts.find((p) => p.type === "month")?.value;

    return `${day} ${month}`;
  };

  const getTimeZoneLabel = () => {
    if (typeof window !== "undefined") {
      const siteContextValue = getSiteContext();

      if (siteContextValue == "monstergulf") {
        // GULF
        return "GST";
      } else if (
        siteContextValue == "monsterthailand" ||
        siteContextValue == "monstervietnam" ||
        siteContextValue == "monsterindonesia"
      ) {
        // SEA UTC+7
        return "ICT";
      } else if (
        siteContextValue == "monstersingapore" ||
        siteContextValue == "monstermalaysia" ||
        siteContextValue == "monsterphilippines" ||
        siteContextValue == "monsterhongkong"
      ) {
        // SEA UTC+8

        const timeFormatMapping: any = {
          monstersingapore: "SGT",
          monstermalaysia: "MYT",
          monsterphilippines: "PHT",
          monsterhongkong: "HKT",
        };

        return timeFormatMapping[siteContextValue];
      }

      return "IST";
    }
  };

  // Close button handler
  const handlePanelClose = () => {
    setIsClosing(true);

    if (typeof window !== "undefined") {
      const existingTempKeys = JSON.parse(
        localStorage?.getItem("tempKeys") || "{}",
      );

      localStorage?.setItem(
        "tempKeys",
        JSON.stringify({ ...existingTempKeys, showPanel: false }),
      );
    }

    setTimeout(() => setShowPanel(false), 400);
  };

  const isNextDay = (start: Date, end: Date) => {
    return start.toDateString() !== end.toDateString();
  };

  // Banner visibility logic
  if (
    !isMounted ||
    !showPanel ||
    !isPlatformMatch(schedule, platform) ||
    now < showFromDateTime || // show only after showFrom
    now > startDateTime // hide once maintenance starts
  ) {
    return null;
  }

  return (
    <div
      className={`rec_top_panel_container ${
        isClosing ? "rec_top_panel_closing" : ""
      }`}
      style={topBannerStyles?.containerStyles || {}}
    >
      <div
        className="rec_top_panel_content_wrapper"
        style={topBannerStyles?.subContainerStyles || {}}
      >
        <div className="rec_top_panel_content">
          <div style={{ flexShrink: 0, display: "flex" }}>
            <ToolkitIcon />
          </div>

          <span>
            {schedule.message ? (
              schedule.message
            ) : (
              <>
                Our platform will be{" "}
                <span className="rec_top_panel_bold">
                  temporarily unavailable
                </span>{" "}
                on{" "}
                <span className="rec_top_panel_bold">
                  {formatDate(startDateTime)}
                </span>{" "}
                {"- "}
                <span className="rec_top_panel_bold rec_top_panel_uppercase">
                  {formatTime(startDateTime)}
                </span>{" "}
                to{" "}
                <span className="rec_top_panel_bold">
                  {isNextDay(startDateTime, endDateTime) ? (
                    <>
                      {formatDate(endDateTime)}
                      {" - "}
                      <span className="rec_top_panel_uppercase">
                        {format24HourWithAmPm(endDateTime)}
                      </span>
                    </>
                  ) : (
                    <span className="rec_top_panel_uppercase">
                      {formatTime(endDateTime)}
                    </span>
                  )}
                </span>
                {" "}{getTimeZoneLabel()} due to planned system upgrades.
              </>
            )}
          </span>
        </div>

        <div
          onClick={handlePanelClose}
          style={{ cursor: "pointer", display: "flex" }}
        >
          <CloseIcon2 />
        </div>
      </div>
    </div>
  );
};

export default TopBanner;
